import {
  Box,
  Button,
  Card,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Item, ItemHistory, ItemHistoryLineType } from './item-types';
import { useEffect, useMemo, useState } from 'react';
import TableWrapperTyped from '../shared/table';
import { dataTypes } from '../shared/table-helpers';
import { ItemAPI } from '../../api/item';
import ItemHistoryLineTable, {
  ItemHistoryLineTableProps,
} from './item-history-line-table';
import Modal from '../shared/modal';
import ItemSHSFTable, { ItemSHSFTableProps } from './item-shsf-table';
import { dataTransformers } from '../shared/data-transformers';
import { colors } from '../shared/ui-extension';

const TableWrapper = TableWrapperTyped as any;

type Location = {
  id: number;
  name: string;
};

export default function ItemHistoryTable({ item }: { item: Item }) {
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [itemHistories, setItemHistories] = useState<ItemHistory[]>([]);
  const handleLocationIdChange = (_: any, val: number) => {
    setSelectedLocationIds(val);
  };
  const [currentlyViewingYear, setCurrentlyViewingYear] = useState<number>(
    new Date().getFullYear()
  );
  const [historyListType, setHistoryListType] = useState<
    (ItemHistoryLineTableProps & { title: string }) | undefined
  >(undefined);
  const hideHistoryList = () => setHistoryListType(undefined);
  const showHistoryList = (
    type: ItemHistoryLineType,
    date: { month: number; year: number }
  ) => {
    let extraCols = [];
    let title = '';
    switch (type) {
      case 'sold':
        extraCols = [
          { id: 'sale_customer', label: 'Customer' },
          { id: 'sale_order_no', label: 'Order #' },
        ];
        title = 'Sold';
        break;
      case 'rma':
        extraCols = [
          { id: 'rma_customer', label: 'Customer' },
          { id: 'rma_order_no', label: 'RMA Order #' },
        ];
        title = 'RMA';
        break;
      case 'received':
        extraCols = [
          { id: 'rcv_supplier_name', label: 'Supplier' },
          { id: 'rcv_container_name', label: 'Truck #' },
          { id: 'rcv_po_number', label: 'PO #' },
        ];
        title = 'Received';
        break;
      case 'adjusted':
        extraCols = [
          { id: 'adj_creator', label: 'User' },
          { id: 'adj_reason', label: 'Reason' },
        ];
        title = 'Adjusted';
        break;
      case 'transferred':
        extraCols = [{ id: 'transfer_no', label: 'Transfer #' }];
        title = 'Transferred';
        break;
      case 'production':
        extraCols = [{ id: 'prod_order_no', label: 'Production Order #' }];
        title = 'Production';
        break;
    }
    setHistoryListType({
      historyType: type,
      month: date.month,
      year: date.year,
      itemId: item.item_id,
      locationId: selectedLocationIds,
      extraColumns: extraCols,
      title,
    });
  };

  const [shsfData, setShsfData] = useState<
    (ItemSHSFTableProps & { title: string }) | undefined
  >(undefined);
  const showShsf = (month: number, year: number) => {
    setShsfData({
      month,
      year,
      locationId: selectedLocationIds,
      itemId: item.item_id,
      title: `Should Have Shipped From - ${dataTypes.monthYear({
        month,
        year,
      })}`,
    });
  };
  const hideShsf = () => setShsfData(undefined);

  const usersDateYear = new Date().getFullYear();
  let yr = new Date().getFullYear();
  let years = [];
  while (yr >= 2022) {
    years.push(yr);
    yr -= 1;
  }

  useEffect(() => {
    setIsLoading(true);
    const getData = async () => {
      const data = await ItemAPI.getItemHistory({
        itemID: item.item_id,
        year: currentlyViewingYear,
      });
      setItemHistories(data.data);
      setIsLoading(false);

      // Get list of locations in this
      // dataset.
      let ids = new Set<number>();
      let locations: Location[] = [];
      for (const h of data.data) {
        if (!ids.has(h.location_id)) {
          locations.push({
            id: h.location_id,
            name: h.location_name,
          });
          ids.add(h.location_id);
        }
      }
      locations.sort((a, b) => a.id - b.id);
      setAvailableLocations(locations);
      if (selectedLocationIds == 0) {
        setSelectedLocationIds(locations[0].id);
      }
    };
    getData();
  }, [item.item_id, currentlyViewingYear]);

  const filteredItemHistories = useMemo(() => {
    let reuseOldMonthsAfterMonthNumber =
      currentlyViewingYear == new Date().getFullYear()
        ? new Date().getMonth() + 1
        : 12;
    return itemHistories
      .filter(
        (item) =>
          selectedLocationIds == item.location_id &&
          (item.year == currentlyViewingYear ||
            item.month > reuseOldMonthsAfterMonthNumber)
      )
      .sort((a, b) => {
        if (a.year != b.year) return b.year - a.year;
        return b.month - a.month;
      })
      .map((item) => {
        return {
          ...item,
          total_shsf_qty: item.shsf_qty,
          startDate: { month: item.month, year: item.year },
        };
      });
  }, [itemHistories, selectedLocationIds]);
  const aggregates = useMemo(() => {
    const currentMonth = new Date().getMonth() + 1;
    return itemHistories
      .filter(
        (item) =>
          selectedLocationIds == item.location_id &&
          (item.year == currentlyViewingYear || item.month > currentMonth)
      )
      .sort((a, b) => b.month + b.year * 12 - (a.month + a.year * 12))
      .map((item) => {
        return {
          ...item,
          startDate: { month: item.month, year: item.year },
        };
      });
  }, [filteredItemHistories]);
  let periodsToShow = [];
  if (aggregates.length > 0) {
    periodsToShow =
      currentlyViewingYear == new Date().getFullYear() ? [3, 6, 12] : [12];
  }
  const aggregateRows = periodsToShow.map((period) => {
    const cap = period - 1;
    const start = aggregates[0];
    const end = aggregates[cap];
    const aggregateRange = `${dataTypes.monthYear(
      end.startDate
    )} - ${dataTypes.monthYear(start.startDate)}`;

    let qty_sold = 0;
    for (let i = 0; i <= cap; i++) {
      qty_sold += aggregates[i].qty_sold;
    }

    return {
      name: `${period} month average`,
      range: aggregateRange,
      qty_sold: qty_sold / period,
    };
  });

  const cols = [
    {
      id: 'startDate',
      label: 'Period',
      dataType: dataTypes.monthYear,
    },
    {
      id: 'begin_qty',
      label: 'Beginning Balance',
      dataType: dataTypes.number,
    },
    {
      id: 'qty_sold',
      label: 'Sold',
      dataType: (
        qty: number,
        row: ItemHistory & { startDate: { month: number; year: number } }
      ) => {
        return (
          <Button onClick={() => showHistoryList('sold', row.startDate)}>
            {dataTypes.number(qty)}
          </Button>
        );
      },
    },
    {
      id: 'qty_received',
      label: 'Received',
      dataType: (
        qty: number,
        row: ItemHistory & { startDate: { month: number; year: number } }
      ) => {
        return (
          <Button onClick={() => showHistoryList('received', row.startDate)}>
            {dataTypes.number(qty)}
          </Button>
        );
      },
    },
    {
      id: 'qty_transfered',
      label: 'Transferred',
      dataType: (
        qty: number,
        row: ItemHistory & { startDate: { month: number; year: number } }
      ) => {
        return (
          <Button onClick={() => showHistoryList('transferred', row.startDate)}>
            {dataTypes.number(qty)}
          </Button>
        );
      },
    },
    {
      id: 'qty_rma',
      label: 'RMA',
      dataType: (
        qty: number,
        row: ItemHistory & { startDate: { month: number; year: number } }
      ) => {
        return (
          <Button onClick={() => showHistoryList('rma', row.startDate)}>
            {dataTypes.number(qty)}
          </Button>
        );
      },
    },
    {
      id: 'qty_adjusted',
      label: 'Adjusted',
      dataType: (
        qty: number,
        row: ItemHistory & { startDate: { month: number; year: number } }
      ) => {
        return (
          <Button onClick={() => showHistoryList('adjusted', row.startDate)}>
            {dataTypes.number(qty)}
          </Button>
        );
      },
    },
    {
      id: 'qty_production',
      label: 'Production',
      dataType: (
        qty: number,
        row: ItemHistory & { startDate: { month: number; year: number } }
      ) => {
        return (
          <Button onClick={() => showHistoryList('production', row.startDate)}>
            {dataTypes.number(qty)}
          </Button>
        );
      },
    },
    {
      id: 'end_qty',
      label: 'End Balance',
      dataType: dataTypes.number,
    },
    {
      id: 'total_shsf_qty',
      label: 'Should Have Shipped From',
      dataType: (
        qty: number,
        row: ItemHistory & { startDate: { month: number; year: number } }
      ) => {
        return (
          <Button onClick={() => showShsf(row.month, row.year)}>
            {dataTypes.number(qty)}
          </Button>
        );
      },
    },
  ];

  let modalTitle = '';
  if (historyListType !== undefined) {
    const dateFormat = dataTypes.monthYear(historyListType);
    modalTitle = `${historyListType.title} — ${dateFormat}`;
  }

  return (
    <Stack useFlexGap gap={2}>
      <Tabs
        value={selectedLocationIds}
        onChange={handleLocationIdChange}
        variant='scrollable'
      >
        {availableLocations.map((loc) => (
          <Tab label={loc.name} value={loc.id} />
        ))}
      </Tabs>
      <Stack
        direction='row'
        useFlexGap
        gap={3}
        justifyContent={'end'}
        alignContent={'top'}
        sx={{ marginRight: '10px', marginLeft: '10px' }}
      >
        {!isLoading && (
          <Stack direction={'row'} useFlexGap gap={4} sx={{ width: '100%' }}>
            {aggregateRows.map((agg) => (
              <Card>
                <Stack>
                  <Typography variant='subtitle2' color={colors.darkGray}>
                    {agg.name}
                  </Typography>
                  <Typography variant='h5'>
                    {dataTransformers.integer(-agg.qty_sold)}
                  </Typography>
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
        <Box>
          <Select
            title='Year'
            value={currentlyViewingYear}
            onChange={(v) => setCurrentlyViewingYear(v.target.value as number)}
          >
            {years.map((yr) => (
              <MenuItem key={yr} value={yr}>
                {yr}
                {yr == usersDateYear ? ' (Rolling 12)' : ''}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Stack>
      <Modal
        title={modalTitle}
        open={historyListType !== undefined}
        setOpen={hideHistoryList}
        allowBackdropClose
      >
        <ItemHistoryLineTable {...historyListType} />
      </Modal>
      <Modal
        title={shsfData?.title ?? 'Should Have Shipped From'}
        open={shsfData !== undefined}
        setOpen={hideShsf}
        allowBackdropClose
      >
        {shsfData ? <ItemSHSFTable {...shsfData} /> : null}
      </Modal>
      {isLoading ? (
        <Stack sx={{ width: '100%', paddingY: 2 }} alignItems={'center'}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack>
          <TableWrapper
            style={{ height: '571px' }}
            transformRows
            columns={cols}
            rows={filteredItemHistories}
          />
        </Stack>
      )}
    </Stack>
  );
}
